import { useRef, type ReactNode } from 'react';

import type { Operation } from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { formatCpfCnpj, formatDateTime, formatMoney } from '@/shared/lib/format';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useEncaixePelaLargura } from '@/shared/ui/useEncaixePelaLargura';

import { MenuDeAcoes, type AcaoDoMenu } from '../../users/MenuDeAcoes';

import { numeroDaCotacaoDaCorretora } from './cotacaoDaCorretora';
import {
  encaixarListaDaCorretora,
  type EncaixeDaListaDaCorretora,
} from './listaDaCorretoraLayout';

/**
 * A lista de cotacoes da corretora numa tabela que cabe na largura recebida (ver
 * listaDaCorretoraLayout.ts). Os dados sao os da lista de antes: numero, tipo, canal,
 * tomador, segurado, modalidade, status, data da cotacao e IS. O que nao cabe numa
 * coluna propria vai para a segunda linha da celula. As acoes ficam no menu da linha,
 * na ultima coluna, que abre por cima da tela.
 */

/** As acoes de cada linha, montadas pela pagina. */
export interface AcoesDaCotacao {
  acoes: (operacao: Operation) => AcaoDoMenu[];
  /** A linha com uma acao em andamento (imprimir a cotacao ou a minuta). */
  ocupada: (operacao: Operation) => boolean;
}

/** Texto em uma linha so: o que nao cabe vira reticencias e aparece no mouse. */
function Linha({
  children,
  className,
  titulo,
}: {
  children: ReactNode;
  className?: string;
  titulo?: string | undefined;
}) {
  return (
    <span
      title={titulo ?? (typeof children === 'string' ? children : undefined)}
      className={cn('block truncate', className)}
    >
      {children}
    </span>
  );
}

function Detalhe({ children, titulo }: { children: ReactNode; titulo?: string }) {
  return (
    <Linha className="text-2xs text-ink-500" titulo={titulo}>
      {children}
    </Linha>
  );
}

function tipoDe(operacao: Operation): string {
  return statusLabel('tipoCotacao', operacao.proposal_type ?? 'apolice');
}

function canalDe(operacao: Operation): string {
  return operacao.channel?.label ?? 'Canal nao definido';
}

function documentoDe(documento: string | undefined | null): string {
  return documento ? formatCpfCnpj(documento) : '';
}

/** Numero da cotacao e, embaixo, o detalhe que a disposicao escolhe. */
function Numero({ operacao, detalhe }: { operacao: Operation; detalhe: string }) {
  return (
    <div className="min-w-0">
      <Linha className="font-medium tabular-nums text-ink-900">
        {numeroDaCotacaoDaCorretora(operacao)}
      </Linha>
      <Detalhe>{detalhe}</Detalhe>
    </div>
  );
}

/** Nome e documento de uma parte, um em cima do outro. */
function Parte({ nome, documento }: { nome: string; documento: string }) {
  return (
    <div className="min-w-0">
      <Linha>{nome}</Linha>
      {documento ? <Detalhe>{documento}</Detalhe> : null}
    </div>
  );
}

function Tomador({ operacao }: { operacao: Operation }) {
  return (
    <Parte
      nome={operacao.policyholder.label}
      documento={documentoDe(operacao.policyholder.document)}
    />
  );
}

function Segurado({ operacao }: { operacao: Operation }) {
  if (!operacao.insured) return <span className="text-ink-400">-</span>;
  return (
    <Parte
      nome={operacao.insured.label}
      documento={documentoDe(operacao.insured.document)}
    />
  );
}

/** A situacao da cotacao. A etiqueta quebra em duas linhas quando a coluna estreita. */
function Status({ operacao }: { operacao: Operation }) {
  return operacao.proposal_status ? (
    <div className="min-w-0">
      <StatusBadge kind="cotacao" value={operacao.proposal_status} />
    </div>
  ) : null;
}

function Valor({ operacao }: { operacao: Operation }) {
  return <Linha className="tabular-nums">{formatMoney(operacao.insured_amount)}</Linha>;
}

function colunaDasAcoes(acoes: AcoesDaCotacao, width: string): Column<Operation> {
  return {
    key: 'acoes',
    header: 'Acoes',
    width,
    render: (operacao) => (
      <MenuDeAcoes
        titulo={`Acoes da cotacao ${numeroDaCotacaoDaCorretora(operacao)}`}
        acoes={acoes.acoes(operacao)}
        ocupado={acoes.ocupada(operacao)}
      />
    ),
  };
}

function colunasAmplas(acoes: AcoesDaCotacao): Column<Operation>[] {
  return [
    {
      key: 'numero',
      header: 'Nro. da Cotacao',
      detail: 'Tipo e canal',
      width: '11rem',
      render: (operacao) => (
        <Numero
          operacao={operacao}
          detalhe={`${tipoDe(operacao)} · ${canalDe(operacao)}`}
        />
      ),
    },
    {
      key: 'tomador',
      header: 'Tomador',
      render: (operacao) => <Tomador operacao={operacao} />,
    },
    {
      key: 'segurado',
      header: 'Segurado',
      render: (operacao) => <Segurado operacao={operacao} />,
    },
    {
      key: 'modalidade',
      header: 'Modalidade',
      width: '14%',
      render: (operacao) => <Linha>{operacao.modality.label}</Linha>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '10.5rem',
      render: (operacao) => <Status operacao={operacao} />,
    },
    {
      key: 'data',
      header: 'Data da cotacao',
      width: '9rem',
      render: (operacao) => <Linha>{formatDateTime(operacao.created_at)}</Linha>,
    },
    {
      key: 'valor',
      header: 'IS',
      align: 'right',
      width: '9rem',
      render: (operacao) => <Valor operacao={operacao} />,
    },
    colunaDasAcoes(acoes, '7.5rem'),
  ];
}

function colunasReunidas(acoes: AcoesDaCotacao): Column<Operation>[] {
  return [
    {
      key: 'numero',
      header: 'Nro. da Cotacao',
      detail: 'Tipo e canal',
      width: '11rem',
      render: (operacao) => (
        <Numero
          operacao={operacao}
          detalhe={`${tipoDe(operacao)} · ${canalDe(operacao)}`}
        />
      ),
    },
    {
      key: 'tomador',
      header: 'Tomador',
      render: (operacao) => <Tomador operacao={operacao} />,
    },
    {
      key: 'segurado',
      header: 'Segurado',
      render: (operacao) => <Segurado operacao={operacao} />,
    },
    {
      key: 'modalidade',
      header: 'Modalidade',
      detail: 'Data da cotacao',
      width: '15%',
      render: (operacao) => (
        <div className="min-w-0">
          <Linha>{operacao.modality.label}</Linha>
          <Detalhe>{formatDateTime(operacao.created_at)}</Detalhe>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '10rem',
      render: (operacao) => <Status operacao={operacao} />,
    },
    {
      key: 'valor',
      header: 'IS',
      align: 'right',
      width: '8.5rem',
      render: (operacao) => <Valor operacao={operacao} />,
    },
    colunaDasAcoes(acoes, '7.5rem'),
  ];
}

/** O que a celula estreita nao mostra fica no titulo, ao passar o mouse. */
function resumoDaCotacao(operacao: Operation): string {
  return [
    tipoDe(operacao),
    canalDe(operacao),
    formatDateTime(operacao.created_at),
    operacao.modality.label,
  ].join(' · ');
}

function colunasEstreitas(acoes: AcoesDaCotacao): Column<Operation>[] {
  return [
    {
      key: 'numero',
      header: 'Nro. da Cotacao',
      detail: 'Modalidade',
      width: '11rem',
      render: (operacao) => (
        <div className="min-w-0" title={resumoDaCotacao(operacao)}>
          <Linha className="font-medium tabular-nums text-ink-900">
            {numeroDaCotacaoDaCorretora(operacao)}
          </Linha>
          <Detalhe>{operacao.modality.label}</Detalhe>
        </div>
      ),
    },
    {
      key: 'tomador',
      header: 'Tomador',
      detail: 'Segurado',
      render: (operacao) => (
        <div className="min-w-0">
          <Linha
            titulo={`${operacao.policyholder.label} ${documentoDe(operacao.policyholder.document)}`}
          >
            {operacao.policyholder.label}
          </Linha>
          <Detalhe>{operacao.insured?.label ?? '-'}</Detalhe>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '10rem',
      render: (operacao) => <Status operacao={operacao} />,
    },
    {
      key: 'valor',
      header: 'IS',
      align: 'right',
      width: '8.5rem',
      render: (operacao) => <Valor operacao={operacao} />,
    },
    colunaDasAcoes(acoes, '7.5rem'),
  ];
}

function colunasMinimas(acoes: AcoesDaCotacao): Column<Operation>[] {
  return [
    {
      key: 'numero',
      header: 'Nro. da Cotacao',
      detail: 'Tomador e IS',
      render: (operacao) => (
        <div className="min-w-0" title={resumoDaCotacao(operacao)}>
          <Linha className="font-medium tabular-nums text-ink-900">
            {numeroDaCotacaoDaCorretora(operacao)}
          </Linha>
          <Detalhe>{`${operacao.policyholder.label} · ${formatMoney(operacao.insured_amount)}`}</Detalhe>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '8.5rem',
      render: (operacao) => <Status operacao={operacao} />,
    },
    colunaDasAcoes(acoes, '7.5rem'),
  ];
}

const colunasDoEncaixe: Record<
  EncaixeDaListaDaCorretora,
  (acoes: AcoesDaCotacao) => Column<Operation>[]
> = {
  ampla: colunasAmplas,
  reunida: colunasReunidas,
  estreita: colunasEstreitas,
  minima: colunasMinimas,
};

export function ListaDaCorretora({
  items,
  loading,
  empty,
  onAbrir,
  acoes,
}: {
  items: Operation[];
  loading: boolean;
  empty: ReactNode;
  onAbrir: (operacao: Operation) => void;
  acoes: AcoesDaCotacao;
}) {
  const area = useRef<HTMLDivElement>(null);
  const encaixe = useEncaixePelaLargura(area, encaixarListaDaCorretora);
  return (
    <div ref={area}>
      <DataTable
        fluida
        columns={colunasDoEncaixe[encaixe](acoes)}
        items={items}
        keyOf={(operacao) => operacao.id}
        loading={loading}
        onRowClick={onAbrir}
        empty={empty}
      />
    </div>
  );
}
